import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Music2, Smile, Star, Gamepad2, CalendarDays, BellRing, Users, Languages, Check, BookOpen, QrCode, Timer, Heart, MessageCircle, Instagram } from "lucide-react";
import "./App.css";
import { translations, IMAGES, LANG_LABELS, FEATURE_OPTIONS } from "./mock/translations";
import AdminDashboard from "./AdminDashboard";
import { Impressum, Datenschutz } from "./Legal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_ICONS = {
  gus_timer: Music2,
  avatar: Smile,
  tokens: Star,
  minigames: Gamepad2,
  calendar: CalendarDays,
  reminders: BellRing,
  multikid: Users,
  bilingual: Languages,
};

const Mascot = ({ size = 38 }) => (
  <img
    className="brand-mascot"
    src={IMAGES.mascotBadge}
    alt="Zubo"
    style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }}
  />
);

function Nav({ lang, setLang, t }) {
  return (
    <header className="nav">
      <div className="nav-inner">
        <div className="brand">
          <Mascot />
          <span>ZUBO<span style={{ fontSize: "12px", verticalAlign: "super" }}>™</span></span>
        </div>
        <nav className="nav-links">
          <a href="#problem">{t.nav1}</a>
          <a href="#ablauf">{t.nav2}</a>
          <a href="#buch">{t.navBook}</a>
          <a href="#ueber">{t.nav3}</a>
        </nav>
        <div className="nav-right">
          <div className="lang-switch" role="group" aria-label="Sprache / Language / Jezik">
            {Object.keys(LANG_LABELS).map((code) => (
              <button
                key={code}
                type="button"
                className={lang === code ? "active" : ""}
                onClick={() => setLang(code)}
              >
                {LANG_LABELS[code]}
              </button>
            ))}
          </div>
          <a className="nav-cta" href="#mitmachen">{t.navcta}</a>
        </div>
      </div>
    </header>
  );
}

// Shared signup form state + submit logic (used by book pre-order and survey).
function useSignupForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (payload) => {
    if (!email) return false;
    setStatus("loading");
    try {
      await axios.post(`${API}/signups`, { email, ...payload });
      setStatus("success");
      setEmail("");
      return true;
    } catch (err) {
      setStatus("error");
      return false;
    }
  };

  return { email, setEmail, status, submit };
}

function Hero({ t }) {
  return (
    <section className="hero">
      <div className="wrap hero-grid">
        <div>
          <span className="eyebrow"><span className="dot" /> <span>{t.heroEyebrow}</span></span>
          <h1>{t.heroH1a}<span className="accent">{t.heroH1accent}</span>{t.heroH1b}</h1>
          <p className="lead">{t.heroLead}</p>
          <div className="hero-ctas">
            <a href="#mitmachen" className="btn btn-primary" data-testid="hero-cta-primary">{t.heroCta1}</a>
            <a href="#unterstuetzen" className="btn btn-ghost" data-testid="hero-cta-secondary">{t.heroCta2}</a>
          </div>
          <p className="hero-note">{t.heroNote}</p>
        </div>
        <div className="hero-art">
          <div className="float-badge b1">{t.badge1}</div>
          <div className="float-badge b2">{t.badge2}</div>
          <img src={IMAGES.heroZubo} alt="Zubo" style={{ width: "100%", maxWidth: "440px", borderRadius: "20px", margin: "0 auto" }} />
        </div>
      </div>
      <svg className="wave" viewBox="0 0 1200 60" preserveAspectRatio="none">
        <path d="M0 30 C 150 60 300 0 450 30 C 600 60 750 0 900 30 C 1000 50 1100 20 1200 30 L1200 60 L0 60 Z" fill="#FFFFFF" />
      </svg>
    </section>
  );
}

// "Šta je ZUBO?" — 5-step how-it-works flow (Lesen → Scannen → Zähneputzen → Sammeln → Dranbleiben).
function Vision({ t }) {
  const flow = [
    { key: "read", img: IMAGES.iconBook, label: t.flow1label, text: t.flow1text },
    { key: "scan", img: IMAGES.iconScan, label: t.flow2label, text: t.flow2text },
    { key: "brush", img: IMAGES.iconBrush, label: t.flow3label, text: t.flow3text },
    { key: "collect", img: IMAGES.iconStar, label: t.flow4label, text: t.flow4text },
    { key: "keep", img: IMAGES.iconHeart, label: t.flow5label, text: t.flow5text },
  ];
  return (
    <section id="vision" data-testid="vision-section" style={{ background: "#fff" }}>
      <div className="wrap">
        <div className="section-head">
          <span className="eyebrow">{t.visionEyebrow}</span>
          <h2>{t.visionH2}</h2>
          <p>{t.visionP}</p>
        </div>
        <div className="flow-grid">
          {flow.map((f) => (
            <div className="flow-step" key={f.key} data-testid={`flow-step-${f.key}`}>
              <img className="flow-icon" src={f.img} alt="" />
              <h3>{f.label}</h3>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Sketches({ t }) {
  return (
    <section id="skizzen" style={{ background: "#fff", paddingTop: "20px" }}>
      <div className="wrap">
        <div className="section-head">
          <span className="eyebrow">{t.sketchEyebrow}</span>
          <h2>{t.sketchH2}</h2>
          <p>{t.sketchP}</p>
        </div>
        <div className="sketch-gallery">
          <figure className="sketch-card">
            <div className="img-box"><img src={IMAGES.sketchZubo} alt="Zubo Charakterskizze" /></div>
            <figcaption>{t.sketchCap1}</figcaption>
          </figure>
          <figure className="sketch-card">
            <div className="img-box"><img src={IMAGES.sketchZahnfee} alt="Zubić Vila putzt Zähne, Skizze" /></div>
            <figcaption>{t.sketchCap2}</figcaption>
          </figure>
        </div>
        <figure className="sketch-feature" data-testid="sketch-feature">
          <img src={IMAGES.sketchFairy} alt="Zubić-Vila fliegt mit Zahnbürste durch eine Reihe lächelnder Zähne" />
          <figcaption>{t.sketchCap3}</figcaption>
        </figure>
      </div>
    </section>
  );
}
function IdeasSurvey({ t, lang }) {
  const { email, setEmail, status, submit } = useSignupForm();
  const [features, setFeatures] = useState([]);
  const [ideas, setIdeas] = useState("");
  const options = FEATURE_OPTIONS[lang] || FEATURE_OPTIONS.de;

  const toggleFeature = (key) => {
    setFeatures((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await submit({ lang, features, preorder_book: false, message: ideas.trim() || null });
    if (ok) { setFeatures([]); setIdeas(""); }
  };

  return (
    <section id="mitmachen" className="ideas-section" data-testid="survey-section" style={{ background: "var(--cream)" }}>
      <div className="wrap">
        <div className="section-head">
          <span className="eyebrow">{t.conceptEyebrow}</span>
          <h2>{t.mitH2}</h2>
          <p>{t.mitP}</p>
        </div>
        <div className="ideas-grid">
          <div className="ideas-aside">
            <div className="ideas-art">
              <img src={IMAGES.groupCharacters} alt="Die ZUBO-Figuren gemeinsam auf der Wiese" />
            </div>
            <p className="ideas-lead">{t.conceptP}</p>
            <ul className="digi-list">
              <li><Check size={17} strokeWidth={3} /> {t.digiLi1}</li>
              <li><Check size={17} strokeWidth={3} /> {t.digiLi2}</li>
              <li><Check size={17} strokeWidth={3} /> {t.digiLi3}</li>
              <li><Check size={17} strokeWidth={3} /> {t.digiLi4}</li>
            </ul>
          </div>
          <div className="mitmachen-card ideas-card" data-testid="survey-card">
            <span className="ideas-badge">{t.ideasBadge}</span>
            {status === "success" ? (
              <p className="signup-success" data-testid="survey-success-message" role="status">✅ {t.waitlistSuccess}</p>
            ) : (
              <form className="signup-form" onSubmit={handleSubmit}>
                <p className="survey-q">{t.surveyQ}</p>
                <div className="option-grid">
                  {options.map((opt) => {
                    const Icon = FEATURE_ICONS[opt.key] || Star;
                    const on = features.includes(opt.key);
                    return (
                      <button
                        type="button"
                        key={opt.key}
                        data-testid={`feature-option-${opt.key}`}
                        className={`option-card ${on ? "on" : ""}`}
                        aria-pressed={on}
                        onClick={() => toggleFeature(opt.key)}
                      >
                        <span className="opt-icon"><Icon size={20} strokeWidth={2.2} /></span>
                        <span className="opt-label">{opt.label}</span>
                        <span className="opt-check"><Check size={14} strokeWidth={3.5} /></span>
                      </button>
                    );
                  })}
                </div>
                <div className="ideas-field">
                  <label htmlFor="own-ideas">{t.ideasLabel}</label>
                  <textarea
                    id="own-ideas"
                    data-testid="survey-ideas-input"
                    rows={3}
                    placeholder={t.ideasPlaceholder}
                    value={ideas}
                    onChange={(e) => setIdeas(e.target.value)}
                  />
                </div>
                <div className="waitlist-form">
                  <label className="visually-hidden" htmlFor="email">{t.formLabel}</label>
                  <input
                    type="email" id="email" name="email"
                    data-testid="survey-email-input"
                    placeholder={t.formPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button type="submit" data-testid="survey-submit-button" disabled={status === "loading"}>
                    {status === "loading" ? "…" : t.formBtn}
                  </button>
                </div>
                <p className="mit-note" style={status === "error" ? { color: "var(--coral-dark)" } : undefined}>
                  {status === "error" ? t.waitlistError : t.fundNote}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Problem({ t }) {
  return (
    <section id="problem" style={{ background: "#fff", paddingTop: 0 }}>
      <div className="wrap">
        <div className="section-head">
          <span className="eyebrow">{t.whyEyebrow}</span>
          <h2>{t.whyH2}</h2>
          <p>{t.whyP}</p>
        </div>
        <div className="split">
          <div className="card problem">
            <h3>{t.beliefEyebrow}</h3>
            <ul>
              <li>{t.belief1}</li>
              <li>{t.belief2}</li>
              <li>{t.belief3}</li>
            </ul>
          </div>
          <div className="card solution">
            <h3>{t.beliefEyebrow2}</h3>
            <ul>
              <li>{t.belief4}</li>
              <li>{t.belief5}</li>
              <li>{t.belief6}</li>
            </ul>
          </div>
        </div>
        <div className="pillars">
          <div className="pillar"><span className="pillar-icon"><Heart size={19} strokeWidth={2.2} /></span><h3>{t.pil1h}</h3><p>{t.pil1p}</p></div>
          <div className="pillar"><span className="pillar-icon"><Users size={19} strokeWidth={2.2} /></span><h3>{t.pil2h}</h3><p>{t.pil2p}</p></div>
          <div className="pillar"><span className="pillar-icon"><Smile size={19} strokeWidth={2.2} /></span><h3>{t.pil3h}</h3><p>{t.pil3p}</p></div>
        </div>
      </div>
    </section>
  );
}

// Belief content moved into the Problem section's two cards; standalone section removed.

function Ablauf({ t }) {  const ecosystem = [
    { key: "book", img: IMAGES.iconBook, h: t.eco1h, p: t.eco1p },
    { key: "calendar", img: IMAGES.iconCalendar, h: t.eco2h, p: t.eco2p },
    { key: "app", img: IMAGES.iconApp, h: t.eco3h, p: t.eco3p },
  ];
  return (
    <section id="ablauf">
      <div className="wrap">
        <div className="section-head">
          <span className="eyebrow">{t.howEyebrow}</span>
          <h2>{t.howH2}</h2>
          <p>{t.howP}</p>
        </div>
        <div className="eco-grid">
          {ecosystem.map((e) => (
            <div className="eco-card" key={e.key} data-testid={`ablauf-step-${e.key}`}>
              <img className="eco-icon-img" src={e.img} alt="" />
              <h3>{e.h}</h3>
              <p>{e.p}</p>
            </div>
          ))}
        </div>
        <div className="vision-bridge" data-testid="vision-bridge">
          <QrCode size={26} strokeWidth={2} />
          <p>{t.visionBridge}</p>
        </div>
      </div>
    </section>
  );
}

// App vision merged into Concept section above.

function About({ t }) {
  return (
    <section id="ueber" style={{ background: "#fff" }}>
      <div className="wrap">
        <div className="about">
          <div className="about-photo">
            <img src={IMAGES.drNeda} alt="Dr. Neda Trivić" />
          </div>
          <div>
            <h2 className="about-title">{t.aboutEyebrow}</h2>
            <p>{t.aboutP2}</p>
            <p>{t.beliefClose}</p>
            <p className="quote">{t.aboutQuote}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Dedicated book pre-order section — separate from the app survey.
function BookPreorder({ t, lang }) {
  const { email, setEmail, status, submit } = useSignupForm();

  const handleSubmit = (e) => {
    e.preventDefault();
    submit({ lang, features: [], preorder_book: true });
  };

  return (
    <section id="buch" data-testid="book-preorder-section" style={{ background: "#fff" }}>
      <div className="wrap">
        <div className="book-panel">
          <div className="book-art">
            <img src={IMAGES.openbookArt} alt="Frida und die Zahnwächter — aufgeschlagenes Buch" />
          </div>
          <div className="book-content">
            <span className="eyebrow"><BookOpen size={15} strokeWidth={2.4} /> <span>{t.bookEyebrow}</span></span>
            <h2>{t.bookH2}</h2>
            <p className="book-lead">{t.bookP}</p>
            <ul className="book-list">
              <li><Check size={17} strokeWidth={3} /> {t.bookLi1}</li>
              <li><Check size={17} strokeWidth={3} /> {t.bookLi2}</li>
              <li><Check size={17} strokeWidth={3} /> {t.bookLi3}</li>
            </ul>
            {status === "success" ? (
              <p className="signup-success" data-testid="book-success-message" role="status">📖 {t.bookSuccess}</p>
            ) : (
              <form className="waitlist-form book-form" onSubmit={handleSubmit}>
                <label className="visually-hidden" htmlFor="book-email">{t.formLabel}</label>
                <input
                  type="email" id="book-email" name="book-email"
                  data-testid="book-email-input"
                  placeholder={t.formPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button type="submit" data-testid="book-submit-button" disabled={status === "loading"}>
                  {status === "loading" ? "…" : t.bookBtn}
                </button>
              </form>
            )}
            <p className="mit-note" style={status === "error" ? { color: "var(--coral-dark)" } : undefined}>
              {status === "error" ? t.waitlistError : t.bookNote}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Startnext crowdfunding teaser — shows live widget when campaign is configured.
function Fund({ t }) {
  const [sn, setSn] = useState(null);
  useEffect(() => {
    axios.get(`${API}/startnext/status`).then((r) => setSn(r.data)).catch(() => setSn({ configured: false }));
  }, []);

  const live = sn && sn.configured;
  const pct = live && sn.goal ? Math.min(100, Math.round((sn.current_funding / sn.goal) * 100)) : 0;

  return (
    <section id="unterstuetzen">
      <div className="wrap">
        <div className="fund-panel">
          <span className="eyebrow" style={{ background: "rgba(255,255,255,.15)", color: "#fff" }}>
            <span className="dot" style={{ background: "#F4B942" }} /> <span>{t.fundEyebrow}</span>
          </span>
          <h2>{t.fundH2}</h2>
          <p>{t.fundP}</p>
          {live ? (
            <div className="sn-widget" data-testid="startnext-widget">
              <div className="sn-amount">
                <strong>{Number(sn.current_funding).toLocaleString("de-AT")} €</strong> {t.snRaised}
              </div>
              {sn.goal ? (
                <div className="sn-bar" aria-label={`${pct}%`}>
                  <div className="sn-fill" style={{ width: `${pct}%` }} />
                </div>
              ) : null}
              <div className="sn-meta">
                {sn.goal ? `${pct}% · ` : ""}{sn.supporters} {t.snSupporters}
              </div>
              <a className="btn btn-primary" href={sn.url} target="_blank" rel="noopener noreferrer" data-testid="startnext-link">
                {t.snOpen}
              </a>
            </div>
          ) : (
            <div className="startnext-slot" id="startnext-widget-placeholder">
              <strong>{t.fundSlotTitle}</strong>
              <span>{t.fundSlotText}</span>
            </div>
          )}
          <a href="#mitmachen" className="btn btn-primary">{t.fundCta}</a>
        </div>
      </div>
    </section>
  );
}

function ShareRow({ t }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "https://zubo.world";
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${t.shareText} ${shareUrl}`)}`;
  const igShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch (e) { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };
  return (
    <div className="share-row" data-testid="share-row">
      <span className="share-title">{t.shareTitle}</span>
      <div className="share-btns">
        <a className="share-btn share-wa" href={waHref} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" aria-label="WhatsApp">
          <MessageCircle size={18} strokeWidth={2.2} /> WhatsApp
        </a>
        <button type="button" className="share-btn share-ig" onClick={igShare} data-testid="share-instagram" aria-label="Instagram">
          <Instagram size={18} strokeWidth={2.2} /> Instagram
        </button>
      </div>
      {copied ? <span className="share-copied" data-testid="share-copied">{t.shareCopied}</span> : null}
    </div>
  );
}

function Footer({ t }) {
  return (
    <footer>
      <div className="wrap">
        <div className="brand" style={{ justifyContent: "center" }}>
          <Mascot size={28} />
          ZUBO™
        </div>
        <p style={{ fontStyle: "italic", color: "#BFE0DC" }} data-testid="footer-tagline">{t.footerTag}</p>
        <p>{t.footerP1}</p>
        <p>
          <a href="mailto:hello.zubo@gmail.com" data-testid="footer-email">hello.zubo@gmail.com</a>
        </p>
        <ShareRow t={t} />
        <p className="footer-legal" data-testid="footer-legal">
          <Link to="/impressum" data-testid="footer-impressum-link">{t.legalImpressum}</Link>
          <span aria-hidden="true"> · </span>
          <Link to="/datenschutz" data-testid="footer-datenschutz-link">{t.legalPrivacy}</Link>
        </p>
        <p style={{ marginTop: "12px", fontSize: "13px" }}>{t.footerP2}</p>
      </div>
    </footer>
  );
}

function Landing() {
  const [lang, setLang] = useState("de");
  const t = translations[lang];
  return (
    <div className="zubo App">
      <Nav lang={lang} setLang={setLang} t={t} />
      <Hero t={t} />
      <Problem t={t} />
      <Ablauf t={t} />
      <Vision t={t} />
      <IdeasSurvey t={t} lang={lang} />
      <Sketches t={t} />
      <BookPreorder t={t} lang={lang} />
      <About t={t} />
      <Fund t={t} />
      <Footer t={t} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
